import React, { useState } from 'react';
import { Box, Button, Typography, LinearProgress, IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { styled, keyframes } from '@mui/system';

const rainbow = keyframes`
  0% { color: #2196f3; }
  25% { color: #64b5f6; }
  50% { color: #90caf9; }
  75% { color: #64b5f6; }
  100% { color: #2196f3; }
`;

const shimmer = keyframes`
  0% { background-position: -280px 0; }
  100% { background-position: 280px 0; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;

const FlashyBox = styled(Box)({
  width: '264px',
  margin: '8px',
  padding: '16px',
  background: 'linear-gradient(135deg, #ffffff, #f0f8ff)',
  border: '1px solid #e1f5fe',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  boxShadow: '0 2px 8px rgba(0, 149, 255, 0.15)',
  animation: `${pulse} 2s infinite ease-in-out`,
  transition: 'all 0.3s ease',
  position: 'relative',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 149, 255, 0.25)',
  }
});

const TextContainer = styled(Box)({
  background: 'linear-gradient(135deg, #000000, #1a1a1a)',
  padding: '12px',
  borderRadius: '8px',
  textAlign: 'center'
});

const PrizeText = styled(Typography)({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#fff',
  animation: `${rainbow} 3s infinite`,
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  marginBottom: '4px'
});

const SubText = styled(Typography)({
  fontSize: '0.875rem',
  color: '#ffffff',
  fontWeight: 500
});

const FlashyButton = styled(Button)({
  background: 'linear-gradient(135deg, #2196f3, #1976d2)',
  color: '#fff',
  padding: '8px 24px',
  fontSize: '0.875rem',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '20px',
  alignSelf: 'center',
  boxShadow: '0 2px 4px rgba(33, 150, 243, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #1976d2, #2196f3)',
    boxShadow: '0 4px 8px rgba(33, 150, 243, 0.4)',
    transform: 'translateY(-1px)'
  }
});

const ProgressContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  marginTop: '4px',
  background: '#fff',
  padding: '16px 12px 12px 12px',
  borderRadius: '8px'
});

const FlashyProgress = styled(LinearProgress)({
  height: '10px',
  borderRadius: '5px',
  backgroundColor: '#333',
  '& .MuiLinearProgress-bar': {
    background: 'linear-gradient(90deg, #2196f3, #64b5f6)',
    backgroundSize: '280px 100%',
    animation: `${shimmer} 2s linear infinite`,
  }
});

const ProgressLabel = styled(Typography)({
  position: 'absolute',
  right: '12px',
  top: '-2px',
  fontSize: '0.75rem',
  color: '#000',
  fontWeight: 600
});

const InfoContainer = styled(Box)({
  position: 'absolute',
  top: '12px',
  right: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '12px',
  background: 'rgba(33, 150, 243, 0.1)',
  '&:hover': {
    background: 'rgba(33, 150, 243, 0.2)',
  }
});

const InfoText = styled(Typography)({
  fontSize: '0.75rem',
  color: '#fff',
  fontWeight: 500
});

//modal

const ModalOverlay = styled(Box)({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)'
  });
  
  const ModalContent = styled(Box)({
    background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
    borderRadius: '16px',
    padding: '24px',
    width: '90%',
    maxWidth: '400px',
    position: 'relative',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    animation: 'slideIn 0.3s ease-out',
    '@keyframes slideIn': {
      from: {
        transform: 'translateY(20px)',
        opacity: 0
      },
      to: {
        transform: 'translateY(0)',
        opacity: 1
      }
    }
  });
  
  const CloseButton = styled(IconButton)({
    position: 'absolute',
    top: '12px',
    right: '12px',
    color: '#666',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      color: '#000'
    }
  });
  
  const StepBox = styled(Box)({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '16px',
    borderRadius: '12px',
    backgroundColor: 'white',
    marginBottom: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'translateX(8px)'
    }
  });
  
  const IconWrapper = styled(Box)({
    padding: '8px',
    borderRadius: '50%',
    backgroundColor: '#e3f2fd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  });

const BingoCreditNavItem = () => {
  const progress = 19;
  const [showModal, setShowModal] = useState(false);

  const steps = [
    {
      icon: <GroupIcon sx={{ fontSize: 24, color: '#2196f3' }} />,
      title: "Join Daily Tournaments",
      description: "Participate in our daily bingo tournaments with other players"
    },
    {
      icon: <EmojiEventsIcon sx={{ fontSize: 24, color: '#2196f3' }} />,
      title: "Win Consecutive Games",
      description: "Win 5 games in a row to multiply your bonus points"
    },
    {
      icon: <EventNoteIcon sx={{ fontSize: 24, color: '#2196f3' }} />,
      title: "Complete Daily Challenges",
      description: "Finish special daily missions to earn extra credit"
    },
    {
      icon: <MonetizationOnIcon sx={{ fontSize: 24, color: '#2196f3' }} />,
      title: "Reach Credit Milestone",
      description: "Accumulate points to unlock the 10,000 credit bonus"
    }
  ];

  return (
    <>
    <FlashyBox>
    <InfoContainer onClick={() => setShowModal(true)} style={{ cursor: 'pointer' }}>
          <InfoText>HOW</InfoText>
          <InfoIcon sx={{ fontSize: '0.875rem', color: '#0288d1' }} />
        </InfoContainer>
      
      <TextContainer>
        <PrizeText>
          WIN 10,000
        </PrizeText>
        <SubText>
          in Bingo Credit
        </SubText>
      </TextContainer>

      <FlashyButton variant="contained">
        Redeem Now
      </FlashyButton>

      <ProgressContainer>
        <ProgressLabel>{`${progress}%`}</ProgressLabel>
        <FlashyProgress variant="determinate" value={progress} />
      </ProgressContainer>
    </FlashyBox>

{showModal && (
    <ModalOverlay onClick={() => setShowModal(false)}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={() => setShowModal(false)}>
          <CloseIcon fontSize="small" />
        </CloseButton>
        
        <Typography variant="h5" sx={{ 
          mb: 3, 
          fontWeight: 600,
          background: 'linear-gradient(135deg, #2196f3, #1976d2)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent'
        }}>
          How to Win 10,000 Bingo Credit
        </Typography>

        {steps.map((step, index) => (
          <StepBox key={index}>
            <IconWrapper>
              {step.icon}
            </IconWrapper>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {step.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                {step.description}
              </Typography>
            </Box>
          </StepBox>
        ))}

        <Typography variant="body2" sx={{ 
          mt: 3, 
          textAlign: 'center',
          color: '#666',
          fontStyle: 'italic'
        }}>
          Complete all steps to unlock your bonus credit!
        </Typography>
      </ModalContent>
    </ModalOverlay>
  )}
  </>
  );
};

export default BingoCreditNavItem;